<script setup lang="ts">
import CommunityButton from '@/components/common/buttons/CommunityButton.vue'
import { app } from '@/composition/app'
import { useI18n } from '@/composition/i18n'
import { useGlobalStore } from '@/store/app'
import { GOKOLLAB_DEFAULT_LOGO } from '@/util/const'
import { getLocationData } from '@ghl/clientportal-core'
import { isGokollab } from '@ghl/clientportal-core/init'
import { UITextSmMedium } from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, ref } from 'vue'

const { t } = useI18n()

const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)

const appStore = useGlobalStore()

const showLoginModal = ref(false)
const loginPage = ref('LOGIN')

const bannerLogo = computed(() => {
  if (isGokollab()) {
    return GOKOLLAB_DEFAULT_LOGO
  } else {
    const locationData = getLocationData()
    return locationData.branding?.portalBackgroundImage
  }
})

function openLogInModal() {
  showLoginModal.value = true
  loginPage.value = 'LOGIN'
}

function openSignUpModal() {
  showLoginModal.value = true
  loginPage.value = 'SIGN_UP'
}

function handleLoginAction() {
  // Close the login modal after successful login
  appStore.closeLoginModal()
  // Redirect or refresh as needed after login
}
</script>

<template>
  <!-- Authentication Banner for Mobile - At the very top -->
  <div
    v-if="appStore.shouldShowLoginBanner"
    class="fixed left-0 right-0 top-0 z-[101] flex items-center justify-between border-b bg-white px-4 py-2"
  >
    <div class="flex items-center">
      <img v-if="bannerLogo" :src="bannerLogo" :alt="t('common.ariaLabels.logo')" class="h-7" />
    </div>
    <div class="flex gap-2">
      <!-- Gokollab Theme Buttons -->
      <template v-if="isGokollab()">
        <CommunityButton
          id="mobile_login_button_banner"
          class="gokollab-login-button !h-8 !bg-white px-2 !text-blue-600 hover:!bg-blue-50"
          button-type="tertiary"
          @click="openLogInModal"
        >
          <UITextSmMedium class="text-sm">{{ t('common.login') }}</UITextSmMedium>
        </CommunityButton>
        <CommunityButton
          id="mobile_signup_button_banner"
          class="!h-8 !bg-blue-600 px-2 !text-white hover:!bg-blue-700"
          button-type="primary"
          @click="openSignUpModal"
        >
          <UITextSmMedium class="text-sm">{{ t('common.signUp') }}</UITextSmMedium>
        </CommunityButton>
      </template>

      <!-- Default Theme Buttons -->
      <template v-else>
        <CommunityButton
          id="mobile_login_button_banner"
          class="!h-8"
          button-type="tertiary"
          @click="openLogInModal"
        >
          <UITextSmMedium class="text-sm">{{ t('common.login') }}</UITextSmMedium>
        </CommunityButton>
        <CommunityButton
          id="mobile_signup_button_banner"
          class="!h-8"
          button-type="primary"
          @click="openSignUpModal"
        >
          <UITextSmMedium class="text-sm">{{ t('common.signUp') }}</UITextSmMedium>
        </CommunityButton>
      </template>
    </div>
  </div>

  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="handleLoginAction"
    @update:show="showLoginModal = false"
  />
</template>

<style scoped>
.gokollab-login-button.community-button-tertiary {
  --n-border: 1px solid #2563eb !important;
  --n-border-hover: 1px solid #2563eb !important;
  --n-border-pressed: 1px solid #2563eb !important;
  --n-border-focus: 1px solid #2563eb !important;
  --n-border-disabled: 1px solid #2563eb !important;
}
</style>
